import type { DemoRunResult } from "./types";

/**
 * Pure derivation of the Live Decision Flow state from a backend run result.
 *
 * Kept out of the component file so it can be unit-tested directly and so the component
 * module exports only components (react-refresh). Nothing here decides an outcome: every
 * branch reads `status`, `http_status`, `degraded`, `replayed`, or `reason_codes` off the
 * response, so the diagram cannot show a path the server did not take.
 *
 * "Step-up Verification" is always in the stage list, but most deployments never light it
 * up: `PolicyEngine.step_up_threshold` is `None` unless `STEP_UP_ENABLED=true` is set
 * alongside the ML backend (ml/AUTOMATION.md), so the middle band the stage represents does
 * not exist for those decisions. The stage renders "skipped" rather than being removed, so
 * the pipeline shape stays legible either way.
 */

export type StageState = "idle" | "running" | "done" | "skipped" | "failed";

export const STAGES = [
  "Transaction",
  "Feature Calculation",
  "Predictor",
  "Policy Engine",
  "Step-up Verification",
] as const;

export type StageName = (typeof STAGES)[number];

export interface FlowState {
  stages: Record<StageName, StageState>;
  terminal: "APPROVE" | "REVIEW" | "ERROR" | null;
  terminalNote: string | null;
  banner: string | null;
}

function uniformStages(state: StageState): Record<StageName, StageState> {
  return {
    Transaction: state,
    "Feature Calculation": state,
    Predictor: state,
    "Policy Engine": state,
    "Step-up Verification": state,
  };
}

const STEP_UP_CODES = new Set(["STEP_UP_PASSED", "STEP_UP_FAILED", "STEP_UP_UNAVAILABLE"]);

export function deriveFlow(result: DemoRunResult | null, running: boolean): FlowState {
  if (running) {
    return { stages: uniformStages("running"), terminal: null, terminalNote: null, banner: null };
  }
  if (!result) {
    return { stages: uniformStages("idle"), terminal: null, terminalNote: null, banner: null };
  }

  // The commit failed, so no decision exists. Rendering a REVIEW here would claim a decision
  // was safely recorded when the server explicitly could not record one.
  if (result.status === "failed") {
    return {
      stages: {
        Transaction: "done",
        "Feature Calculation": "done",
        Predictor: "done",
        "Policy Engine": "failed",
        "Step-up Verification": "skipped",
      },
      terminal: "ERROR",
      terminalNote:
        result.http_status === 503
          ? "판정을 장부에 확정하지 못해 송금이 처리되지 않았습니다. 돈은 그대로 있습니다."
          : result.error_detail ?? "요청이 처리되지 않았습니다.",
      banner: null,
    };
  }

  const decision = result.decisions[result.decisions.length - 1];
  if (!decision) {
    return {
      stages: { ...uniformStages("idle"), Transaction: "done" },
      terminal: null,
      terminalNote: null,
      banner: null,
    };
  }

  const modelUnavailable = decision.reason_codes.includes("MODEL_UNAVAILABLE");
  const featureUnavailable = decision.reason_codes.includes("FEATURE_UNAVAILABLE");
  const stepUpPassed = decision.reason_codes.includes("STEP_UP_PASSED");
  const stepUpFailed = decision.reason_codes.includes("STEP_UP_FAILED");
  const stepUpUnavailable = decision.reason_codes.includes("STEP_UP_UNAVAILABLE");
  const stepUpRan = decision.reason_codes.some((c) => STEP_UP_CODES.has(c));

  const stages: Record<StageName, StageState> = {
    Transaction: "done",
    "Feature Calculation": featureUnavailable ? "failed" : "done",
    Predictor: modelUnavailable ? "failed" : featureUnavailable ? "skipped" : "done",
    "Policy Engine": "done",
    "Step-up Verification": stepUpRan ? (stepUpPassed ? "done" : "failed") : "skipped",
  };

  let banner: string | null = null;
  if (decision.replayed) {
    banner = "멱등 재전송: 새로 판정하지 않고 최초 판정을 그대로 반환했습니다.";
  } else if (decision.degraded && modelUnavailable) {
    banner = "Predictor 장애 — 자동 승인으로 흘리지 않고 REVIEW로 안전하게 전환했습니다.";
  } else if (decision.degraded) {
    banner = "구성요소 장애 — REVIEW로 안전하게 전환했습니다.";
  } else if (stepUpPassed) {
    banner = "본인 확인 자동 통과 — 사람 손 0건. 원래라면 사람이 봐야 했던 건입니다.";
  } else if (stepUpFailed) {
    banner = "본인 확인 실패 — 사람 검토로 전달했습니다.";
  } else if (stepUpUnavailable) {
    banner = "본인 확인을 완료할 수 없어 안전하게 사람 검토로 전달했습니다.";
  }

  return {
    stages,
    terminal: decision.outcome === "APPROVE" ? "APPROVE" : "REVIEW",
    terminalNote:
      decision.outcome === "APPROVE"
        ? "FDS가 후속 거래 처리를 허용했습니다. (자금이체 완료를 뜻하지 않습니다.)"
        : "Human review required — case management is outside P1.",
    banner,
  };
}
